import React, { useState, useEffect } from 'react';
import { BrickCharts, ChartSource } from 'brickcharts/dist/brickcharts.es.js';

function BillboardChart({ chartType = 'hot-100' }) {
  const [chartData, setChartData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fetchTime, setFetchTime] = useState(null);

  useEffect(() => {
    const fetchChartData = async () => {
      try {
        setLoading(true);
        setError(null);
        const startTime = Date.now();
        
        // Use our Node.js proxy server instead of direct Billboard API
        const response = await fetch(`http://localhost:3000/api/billboard/${chartType}`);
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        const data = await response.json();
        const endTime = Date.now();
        
        // Transform the data to match BrickCharts format
        const transformedData = {
          chartType: chartType,
          date: new Date(data.week),
          entries: data.songs.map(song => ({
            rank: song.rank,
            title: song.title,
            artist: song.artist,
            lastWeek: song.position?.positionLastWeek || null,
            peakPosition: song.position?.peakPosition || null,
            weeksOnChart: song.position?.weeksOnChart || null,
            chartDate: new Date(data.week),
            source: 'billboard',
            metadata: {
              cover: song.cover,
              originalData: song
            }
          })),
          source: 'billboard',
          totalEntries: data.songs.length,
          metadata: {
            week: data.week,
            previousWeek: data.previousWeek,
            nextWeek: data.nextWeek
          }
        };
        
        setChartData(transformedData);
        setFetchTime(endTime - startTime);
      } catch (error) {
        setError(error.message);
        console.error('Billboard chart error:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchChartData();
  }, [chartType]);

  const handleRetry = () => {
    setLoading(true);
    setError(null);
    fetchChartData();
  };

  if (loading) {
    return (
      <div className="chart-container">
        <div className="loading-spinner">
          <div className="spinner"></div>
          <p>Loading Billboard chart data...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chart-container">
        <div className="error-message">
          <h3>Error Loading Chart</h3>
          <p>{error}</p>
          <button onClick={handleRetry} className="retry-button">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!chartData) {
    return (
      <div className="chart-container">
        <div className="no-data">
          <p>No chart data available</p>
        </div>
      </div>
    );
  }

  return (
    <div className="chart-container">
      <div className="chart-header">
        <h2>{chartData.chartType.replace('-', ' ').toUpperCase()} Chart</h2>
        <div className="chart-meta">
          <span className="date">Week of {chartData.date.toLocaleDateString()}</span>
          <span className="fetch-time">Loaded in {fetchTime}ms</span>
          <span className="total-entries">{chartData.totalEntries} entries</span>
        </div>
      </div>
      
      <div className="chart-list">
        {chartData.entries.slice(0, 20).map((entry, index) => (
          <div key={index} className="chart-entry">
            <div className="rank">#{entry.rank}</div>
            <div className="entry-info">
              <div className="title">{entry.title}</div>
              <div className="artist">{entry.artist}</div>
            </div>
            <div className="entry-stats">
              {entry.lastWeek && (
                <span className={`position-change ${entry.rank < entry.lastWeek ? 'up' : entry.rank > entry.lastWeek ? 'down' : 'same'}`}>
                  {entry.rank < entry.lastWeek ? '↑' : entry.rank > entry.lastWeek ? '↓' : '='} {Math.abs(entry.rank - entry.lastWeek)}
                </span>
              )}
              {entry.peakPosition && (
                <span className="peak">Peak: #{entry.peakPosition}</span>
              )}
              {entry.weeksOnChart && (
                <span className="weeks">{entry.weeksOnChart} weeks</span>
              )}
            </div>
          </div>
        ))}
      </div>
      
      {chartData.entries.length > 20 && (
        <div className="chart-footer">
          <p>Showing top 20 of {chartData.totalEntries} entries</p>
        </div>
      )}
    </div>
  );
}

export default BillboardChart; 